import { languageEnum, themeModelType } from "./global";
import { planEnum } from "./subscription";
import { z } from "zod";

/**
 * Timezones must be resolvable by `Intl` - unknown ids (typos, "GMT+1", the
 * deprecated "Factory" zone) silently produce Invalid Dates in the overdue-status
 * crons instead of failing at write time.
 */
export const isValidTimezone = (timezone: string): boolean => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

/**
 * Mirrors `legalFormEnum` in @billingnow/db. Onboarding writes "individual" or
 * "sarl"; the accounting setup wizard refines it (see docs/accounting-module.md).
 */
export const legalFormEnum = z.enum([
  "individual",
  "partnership",
  "sarl",
  "sa",
  "cooperative",
  "association",
  "foundation",
]);
export type LegalForm = z.infer<typeof legalFormEnum>;

export const createOrganizationSchema = z.object({
  name: z
    .string({ invalid_type_error: "Organization name must be a string" })
    .min(1, { message: "Organization name is required" })
    .max(255, { message: "Organization name must be less than 255 characters" }),
  taxId: z.string({ invalid_type_error: "Tax ID must be a string" }).optional().nullable(),
  addressLine1: z
    .string({ invalid_type_error: "Address line 1 must be a string" })
    .max(255, { message: "Address line 1 must be less than 255 characters" }),
  addressLine2: z
    .string({ invalid_type_error: "Address line 2 must be a string" })
    .max(255, { message: "Address line 2 must be less than 255 characters" })
    .optional()
    .nullable()
    .or(z.literal("")),
  zipcode: z
    .string({ invalid_type_error: "Zipcode must be a string" })
    .max(20, { message: "Zipcode must be less than 20 characters" }),
  state: z
    .string({ invalid_type_error: "State must be a string" })
    .max(100, { message: "State must be less than 100 characters" }),
  city: z
    .string({ invalid_type_error: "City must be a string" })
    .max(100, { message: "City must be less than 100 characters" }),
  country: z
    .string({ invalid_type_error: "Country must be a string" })
    .min(1, { message: "Country is required" })
    .max(100, { message: "Country must be less than 100 characters" }),
  phone: z
    .string({ invalid_type_error: "Phone must be a string" })
    .regex(/^[\d\s\-\+\(\)]+$/, { message: "Invalid phone number format" })
    .or(z.literal(""))
    .optional()
    .nullable(),
  email: z
    .string({ invalid_type_error: "Email must be a string" })
    .email({ message: "Email must be a valid email address" })
    .or(z.literal(""))
    .optional()
    .nullable(),
  website: z
    .string({ invalid_type_error: "Website must be a string" })
    .url({ message: "Invalid website URL" })
    .optional()
    .nullable()
    .or(z.literal("")),
  logo: z
    .string({ invalid_type_error: "Logo must be a string" })
    .url({ message: "Invalid logo URL" })
    .nullable()
    .optional()
    .nullable()
    .or(z.literal("")),
  additionalInformation: z
    .string({ invalid_type_error: "Additional information must be a string" })
    .max(1000, { message: "Additional information must be less than 1000 characters" })
    .optional()
    .nullable()
    .or(z.literal("")),
  legalName: z.string({ invalid_type_error: "Legal name must be a string" }).optional().nullable(),
  professionalTelephoneNumber: z
    .string({ invalid_type_error: "Professional telephone number must be a string" })
    .regex(/^[\d\s\-\+\(\)]+$/, { message: "Invalid professional telephone number format" })
    .or(z.literal(""))
    .optional()
    .nullable(),
  numberOfEmployees: z
    .number({ invalid_type_error: "Number of employees must be a number" })
    .int({ message: "Number of employees must be a whole number" })
    .positive({ message: "Number of employees must be positive" })
    .optional()
    .nullable(),
  currency: z
    .string({ invalid_type_error: "Currency must be a string" })
    .min(1, { message: "Currency is required" })
    .length(3, { message: "Currency must be a 3-character code" }),
  timezone: z
    .string({ invalid_type_error: "Timezone must be a string" })
    .min(1, { message: "Timezone is required" })
    .refine(isValidTimezone, { message: "Timezone must be a valid IANA timezone (e.g. Europe/Zurich)" }),
  restricted: z.boolean({ invalid_type_error: "Restricted must be a boolean" }).default(false).optional(),
  legalForm: legalFormEnum.optional().nullable(),
  // AI-generated digest of the organization's website (onboarding scrape)
  knowledgeBase: z
    .string({ invalid_type_error: "Knowledge base must be a string" })
    .max(8000, { message: "Knowledge base must be less than 8000 characters" })
    .optional()
    .nullable()
    .or(z.literal("")),
});

export const updateOrganizationSchema = createOrganizationSchema.partial().extend({
  id: z.string({ invalid_type_error: "ID must be a string" }),
});

export const getOrganizationSchema = z.object({
  id: z.string({ invalid_type_error: "ID must be a string" }),
});

export const deleteOrganizationSchema = z.object({
  id: z.string({ invalid_type_error: "ID must be a string" }),
});

export type CreateOrganizationInput = z.infer<typeof createOrganizationSchema>;
export type UpdateOrganizationInput = z.infer<typeof updateOrganizationSchema>;
export type ZodCreateOrganizationSchema = z.infer<typeof createOrganizationSchema>;
export type ZodUpdateOrganizationSchema = z.infer<typeof updateOrganizationSchema>;
export type ZodGetOrganizationSchema = z.infer<typeof getOrganizationSchema>;
export type ZodDeleteOrganizationSchema = z.infer<typeof deleteOrganizationSchema>;

export const organizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  taxId: z.string().nullable().optional(),
  addressLine1: z.string(),
  addressLine2: z.string().nullable().optional(),
  zipcode: z.string(),
  state: z.string(),
  city: z.string(),
  country: z.string(),
  phone: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  website: z.string().nullable().optional(),
  logo: z.string().nullable().optional(),
  additionalInformation: z.string().nullable().optional(),
  knowledgeBase: z.string().nullable().optional(),
  legalName: z.string().nullable().optional(),
  professionalTelephoneNumber: z.string().nullable().optional(),
  numberOfEmployees: z.number().nullable().optional(),
  currency: z.string(),
  timezone: z.string(),
  restricted: z.boolean(),
  legalForm: legalFormEnum.nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const organizationPlanSchema = z.object({
  id: z.string(),
  ownerId: z.string(),
  organizationIds: z.array(z.string()),
  memberIds: z.array(z.string()),
  plan: planEnum,
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const createOrganizationPlanSchema = z.object({
  ownerId: z.string(),
  organizationIds: z.array(z.string()).default([]),
  memberIds: z.array(z.string()).default([]),
  plan: planEnum.default("lite"),
});

export const updateOrganizationPlanSchema = createOrganizationPlanSchema.partial().extend({
  id: z.string({ invalid_type_error: "ID must be a string" }),
});

export const organizationInvoiceTemplateSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  model: themeModelType,
  primaryColor: z.string(),
  textBeforeItems: z.string().optional().nullable(),
  textAfterItems: z.string().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const updateOrganizationTemplateSchema = z.object({
  model: themeModelType,
  language: languageEnum,
  primaryColor: z.string(),
  textBeforeItems: z.string().optional().nullable(),
  textAfterItems: z.string().optional().nullable(),
});

export type OrganizationSchema = z.infer<typeof organizationSchema>;
export type OrganizationPlanSchema = z.infer<typeof organizationPlanSchema>;
export type CreateOrganizationPlanSchema = z.infer<typeof createOrganizationPlanSchema>;
export type UpdateOrganizationPlanSchema = z.infer<typeof updateOrganizationPlanSchema>;
export type OrganizationInvoiceTemplateSchema = z.infer<typeof organizationInvoiceTemplateSchema>;
export type UpdateOrganizationTemplateSchema = z.infer<typeof updateOrganizationTemplateSchema>;

export const paymentPeriodEnum = z.enum(["month", "year"]);
export const upgradePlanSchema = z.object({
  plan: planEnum,
  paymentPeriod: paymentPeriodEnum,
});

export type PaymentPeriodEnum = z.infer<typeof paymentPeriodEnum>;
export type UpgradePlanSchema = z.infer<typeof upgradePlanSchema>;

export const onboardingOrganizationSchema = createOrganizationSchema.merge(updateOrganizationTemplateSchema);
export type OnboardingOrganizationSchema = z.infer<typeof onboardingOrganizationSchema>;

export const createOrganizationSchemaDefaultValues: ZodCreateOrganizationSchema = {
  name: "",
  addressLine1: "",
  addressLine2: "",
  zipcode: "",
  state: "",
  city: "",
  country: "",
  phone: undefined,
  email: "",
  website: "",
  logo: "",
  additionalInformation: "",
  legalName: "",
  professionalTelephoneNumber: undefined,
  numberOfEmployees: undefined,
  currency: "USD",
  timezone: "",
  restricted: false,
  legalForm: undefined,
};
export const createOrganizationTemplateSchemaDefaultValues: UpdateOrganizationTemplateSchema = {
  model: "default",
  language: "en",
  primaryColor: "#635CFF",
};
